import { Component, ElementRef, HostListener, Input, OnChanges, ViewChild } from '@angular/core';
import { ColorPalette, DEFAULT_TEXT_COLORS, PageSection } from '../../../models/end-client-reporting-package';
import { CORRECTION_NAMES, Direction } from '../crosstab-stats';
import {
  CrosstabExplore, ExploreHandle, backTo, drilledInto, withoutStep,
} from '../crosstab-explore';
import {
  ChartSeries, CrosstabColumn, CrosstabMark, CrosstabTableSettings, ReportBlock, Subgroup, readableInk, tint,
} from '../report-aggregation';
import type { ReportSelectOption } from '../report-select.component';
import { csvCell } from '../weighting';
import { textOn } from './summary-boxes.component';

/** How far the banded row's colour is pushed toward white. */
const ROW_TINT = 0.92;

/** The palest and the strongest a heat-shaded cell gets: never so strong the figure is hard to read. */
const HEAT_PALEST = 0.97;
const HEAT_STRONGEST = 0.5;

/** How far toward white a significance tint sits behind its figure. */
const SIGNIFICANCE_TINT = 0.78;

/** A significance tag's fill and outline: pale enough for the deck's ink to read on any palette. */
const TAG_FILL = 0.84;
const TAG_LINE = 0.62;

/** Past this many columns a nested table reads worse than its slices one at a time. */
const NESTED_COLUMN_WARNING = 20;

/** The most small tables a grid lays out before it says the rest are in the tabs. */
const GRID_LIMIT = 12;

/** The Columns picker's "no columns" choice. Not a question key: none starts with a colon. */
const TOTAL_ONLY = ':total';

/** How many significant differences are spelled out under a table. More is a second table. */
const MAX_DIFFERENCES = 3;

/** One column of a table: a subgroup of respondents, and its own base. */
interface TableColumn {
  name: string;
  /** Respondents, unweighted -- the base a reader judges a column's size by. */
  base: number;
  /** The weighted base, shown beside it when the deck is weighted. */
  weightedBase: number;
  effectiveBase: number;
  /** The all-respondents column, which every other column is read against. */
  total: boolean;
  letter: string;
  lowBase: boolean;
  caution: boolean;
  /** What drilling into it filters to; empty where it cannot be drilled. */
  selection: Subgroup[];
}

interface TableCell {
  /** What the cell reads as -- a percentage, a mean, or a count. */
  value: string;
  /** The count printed under it, when the block asks for both. Empty otherwise. */
  count: string;
  /** The columns this cell is significantly higher than, as their letters. */
  letters: string;
  /** The same, one entry per column: a letter past Z is two characters. */
  beats: string[];
  direction: Direction;
  /** Said out loud for a screen reader, since the letters and arrows are only marks. */
  spoken: string;
  shade: string;
  /** The ink that reads best on `shade`, where the cell has one. */
  ink: string;
  /** Too few respondents behind it to print, under the report's cell minimum. */
  suppressed: boolean;
  /** Respondents behind the figure, unweighted -- what a drill into this cell keeps. */
  n: number;
  /** The figure itself, for sorting. */
  sortValue: number;
}

interface TableRow {
  label: string;
  kind: 'answer' | 'net' | 'mean';
  cells: TableCell[];
  /** Drillable rows only: the answer down the side. */
  answer?: string;
}

/** The questions the columns come from, as a header row spanning each one's columns. */
interface BannerGroup {
  label: string;
  span: number;
}

/** One table to draw. A sliced crosstab shown as tabs or a grid is several. */
interface TableModel {
  /** The slice it is, when the table has a third variable shown as tabs or a grid. */
  slice: { label: string; n: number; selection: Subgroup[] } | null;
  columns: TableColumn[];
  rows: TableRow[];
  banner: BannerGroup[];
  /** Where the nets and mean start, so the rule above them is drawn once. -1 when there are none. */
  firstSummary: number;
  caption: string;
  /** The answer drilled into, when every sibling is shown and this is the one. */
  highlighted: boolean;
  /** The largest significant differences, in words, for a reader who will not scan every cell. */
  differences: string[];
}

/** What a click on a heading or a cell is about to do, shown before it does it. */
interface PendingDrill {
  selection: Subgroup[];
  /** Respondents the drill would keep, unweighted. */
  n: number;
  /** "Show every answer of this question" -- offered where the drill is one answer of one question. */
  siblingsOf: string | null;
  /** Sorting by a column is offered from the column's own heading. */
  sortColumn: number | null;
  top: number;
  left: number;
  trigger: HTMLElement | null;
}

/**
 * A crosstab. One question's answers down the side, subgroups of respondents across the top, with
 * nets and a mean under the answers where the block asks for them, sliced by a third question where
 * it has one.
 *
 * It computes nothing. `seriesForBlock` has already done the arithmetic and the significance tests --
 * one series per column, every series aligned to one spine of rows -- so this walks that structure
 * and formats it. That is the point of routing a table through the same aggregation as the charts: a
 * figure in this table and the same figure in the chart beside it cannot disagree, because there is
 * only one of it.
 *
 * Where the deck hands it an `explore`, a reader can drill: click a heading or a cell, see what it
 * would filter to and how many respondents that leaves, and open the table again on just them. The
 * drill itself is the deck's to apply, since only the deck holds the respondents.
 */
@Component({
  selector: 'app-data-table-block',
  standalone: false,
  template: `
    <div #region class="xt" tabindex="-1" [attr.aria-label]="caption"
         (keydown)="guardKeys($event)"
         (keydown.alt.arrowleft)="stepBack($event)">
      <!-- What a drill, a step back or a copy just did, for a screen reader: the table changes
           under the reader, and nothing else would say so. -->
      <p class="sr-only" aria-live="polite">{{ announcement }}</p>
      <!-- The table is the default view: no furniture over it until it is asked for. The one
           exception is a drill, since a filtered table has to say it is filtered. -->
      @if (!compact) {
        <div class="xt-top">
          @if (path.length) {
            <nav class="crumbs" aria-label="Filters on this table">
              <span class="crumbs-label">Filtered to</span>
              @for (step of path; track $index; let i = $index; let last = $last) {
                @if (i > 0) {
                  <i class="fas fa-chevron-right crumb-sep" aria-hidden="true"></i>
                }
                <span class="crumb-chip" [class.is-current]="last">
                  <button type="button" class="crumb"
                          [attr.aria-current]="last ? 'step' : null"
                          [title]="questionName(step.variableKey) + ': ' + step.answer"
                          (click)="goTo(i + 1)">
                    {{ step.answer }}
                    <span class="crumb-n" [class.is-caution]="statusOf(step.n) === 'caution'" [class.is-low]="statusOf(step.n) === 'low'">{{ step.n }}</span>
                  </button>
                  <button type="button" class="crumb-x"
                          [attr.aria-label]="'Remove the filter ' + step.answer"
                          (click)="removeStep(i)">
                    <i class="fas fa-xmark" aria-hidden="true"></i>
                  </button>
                </span>
              }
              <button type="button" class="crumb-reset" (click)="reset()">Reset</button>
            </nav>
          }
          <button type="button" class="xt-toggle"
                  [class.is-open]="expanded"
                  [attr.aria-expanded]="expanded"
                  [attr.aria-controls]="panelId + '-options'"
                  (click)="expanded = !expanded">
            <i class="fas fa-sliders" aria-hidden="true"></i>
            <span>Table options</span>
            @if (!expanded && footnotes.length) {
              <span class="xt-count">{{ footnotes.length }} note{{ footnotes.length === 1 ? '' : 's' }}</span>
            }
            <i class="fas fa-chevron-down xt-chevron" aria-hidden="true"></i>
          </button>
        </div>

        @if (expanded) {
          <div class="xt-panel" [id]="panelId + '-options'">
            <!-- Three bands: what the table shows, what its marks mean, and taking it away. -->
            @if (explore) {
              <div class="xt-section" role="group" [attr.aria-labelledby]="panelId + '-view'">
                <p class="xt-section-title" [id]="panelId + '-view'">View</p>
                <div class="xt-picks">
                  <div class="xt-pick">
                    <span class="xt-pick-label" [id]="panelId + '-rows'">Rows</span>
                    <or-report-select [labelledBy]="panelId + '-rows'"
                                      [options]="rowOptions"
                                      [value]="rowKey"
                                      (selectedChange)="setRows($event)">
                    </or-report-select>
                  </div>
                  <div class="xt-pick">
                    <span class="xt-pick-label" [id]="panelId + '-cols'">Columns</span>
                    <or-report-select [labelledBy]="panelId + '-cols'"
                                      [options]="columnOptions"
                                      [value]="columnValue"
                                      placeholder="Total only"
                                      (selectedChange)="setColumns($event)">
                    </or-report-select>
                  </div>
                  <div class="xt-pick">
                    <span class="xt-pick-label" [id]="panelId + '-split'">Split by</span>
                    <div class="xt-pick-row">
                      <or-report-select [labelledBy]="panelId + '-split'"
                                        [options]="splitOptions"
                                        [value]="layerKey"
                                        placeholder="None"
                                        (selectedChange)="setSplit($event)">
                      </or-report-select>
                      @if (sliced) {
                        <div class="segmented" role="group" aria-label="Show the slices as">
                          @for (choice of views; track choice.value) {
                            <button type="button" [attr.aria-pressed]="view === choice.value" [title]="choice.hint"
                                    (click)="setView(choice.value)">
                              <i class="fas" [ngClass]="choice.icon" aria-hidden="true"></i><span class="sr-only">{{ choice.text }}</span>
                            </button>
                          }
                        </div>
                      }
                    </div>
                  </div>
                </div>
                <div class="xt-explore">
                  <p class="xt-hint">
                    <i class="fas fa-hand-pointer mr-1" aria-hidden="true"></i>Select a column, a row or a figure to drill into those respondents.
                  </p>
                  @if (path.length || !pristine) {
                    <div class="xt-explore-actions">
                      @if (path.length) {
                        <button type="button" class="xt-chip" [attr.aria-pressed]="siblings" (click)="toggleSiblings()">
                          <i class="fas fa-grip mr-1" aria-hidden="true"></i>All {{ questionName(path[path.length - 1].variableKey) }}
                        </button>
                      }
                      @if (!pristine && !path.length) {
                        <button type="button" class="xt-chip" (click)="reset()">
                          <i class="fas fa-rotate-left mr-1" aria-hidden="true"></i>Reset
                        </button>
                      }
                      @if (!pristine && explore!.saveAsTable) {
                        <button type="button" class="xt-chip is-primary" (click)="explore!.saveAsTable!()">
                          <i class="fas fa-thumbtack mr-1" aria-hidden="true"></i>Save as table
                        </button>
                      }
                    </div>
                  }
                </div>
              </div>
            }

            @if (showLetters || crosstab?.sigTest === 'total' || legend || footnotes.length) {
              <div class="xt-section" role="group" [attr.aria-labelledby]="panelId + '-notes'">
                <p class="xt-section-title" [id]="panelId + '-notes'">Key &amp; notes</p>
                @if (showLetters || crosstab?.sigTest === 'total' || legend) {
                  <div class="xt-legends">
                    @if (showLetters || crosstab?.sigTest === 'total' || legend === 'significance') {
                      <div class="legend" aria-hidden="true">
                        @if (showLetters) {
                          <span class="sig-tag is-letters">B</span><span class="mr-3">higher than column B</span>
                        }
                        @if (crosstab?.sigTest === 'total' || legend === 'significance') {
                          <span class="sig-tag is-up">▲</span><span class="mr-2">above everyone else</span>
                          <span class="sig-tag is-down">▼</span><span>below</span>
                        }
                      </div>
                    }
                    @if (legend === 'heat') {
                      <div class="legend" aria-hidden="true">
                        <span class="legend-ramp" [style.background]="heatRamp"></span>
                        <span>Lower</span><span class="ml-auto">Higher</span>
                      </div>
                    }
                  </div>
                }
                @if (footnotes.length) {
                  <ul class="footnotes">
                    @for (note of footnotes; track note) {
                      <li>{{ note }}</li>
                    }
                  </ul>
                }
              </div>
            }

            @if (tables.length) {
              <div class="foot-actions">
                <button type="button" class="xt-chip" (click)="copy()">
                  <i class="fas mr-1" [class.fa-copy]="!copied" [class.fa-check]="copied" aria-hidden="true"></i>{{ copied ? 'Copied' : 'Copy table' }}
                  <span class="sr-only"> {{ caption }} for a spreadsheet</span>
                </button>
                <button type="button" class="xt-chip" (click)="download()">
                  <i class="fas fa-download mr-1" aria-hidden="true"></i>Download CSV
                  <span class="sr-only"> of {{ caption }}</span>
                </button>
              </div>
            }
          </div>
        }
      }

      @if (view === 'tabs' && tables.length > 1) {
        <div class="slice-tabs" role="tablist" [attr.aria-label]="'Slices by ' + layerName">
          @for (table of tables; track $index; let i = $index) {
            <button type="button" role="tab"
                    [id]="tabId(i)"
                    [attr.aria-selected]="i === activeSlice"
                    [attr.aria-controls]="panelId"
                    [attr.tabindex]="i === activeSlice ? 0 : -1"
                    (click)="activeSlice = i"
                    (keydown.arrowright)="moveTab(1, $event)"
                    (keydown.arrowleft)="moveTab(-1, $event)">
              {{ table.slice?.label }} <span class="tab-n">{{ table.slice?.n }}</span>
            </button>
          }
        </div>
        <div role="tabpanel" [id]="panelId" [attr.aria-labelledby]="tabId(activeSlice)">
          <ng-container *ngTemplateOutlet="tableTpl; context: { $implicit: tables[activeSlice], index: activeSlice }"></ng-container>
        </div>
      } @else if (view === 'grid') {
        <div class="slice-grid">
          @for (table of shownTables; track $index; let i = $index) {
            <section class="slice-card" [class.is-highlighted]="table.highlighted"
                     [attr.aria-label]="table.slice?.label">
              <header class="slice-head">
                @if (explore && table.slice && siblings) {
                  <button type="button" class="slice-title" (click)="focusSibling(table)">
                    {{ table.slice.label }}
                  </button>
                } @else {
                  <span class="slice-title">{{ table.slice?.label }}</span>
                }
                <span class="slice-n" [class.is-caution]="statusOf(table.slice?.n ?? 0) === 'caution'" [class.is-low]="statusOf(table.slice?.n ?? 0) === 'low'">n={{ table.slice?.n }}</span>
              </header>
              <ng-container *ngTemplateOutlet="tableTpl; context: { $implicit: table, index: i, mini: true }"></ng-container>
            </section>
          }
        </div>
        @if (tables.length > shownTables.length) {
          <p class="xt-hint mt-2">Showing {{ shownTables.length }} of {{ tables.length }} slices. Switch to tabs to read the rest.</p>
        }
      } @else {
        @for (table of tables; track $index; let i = $index) {
          <ng-container *ngTemplateOutlet="tableTpl; context: { $implicit: table, index: i }"></ng-container>
        }
      }

      @if (pending; as drill) {
        <div #popover class="drill-pop" role="dialog" aria-modal="false" tabindex="-1"
             [attr.aria-labelledby]="panelId + '-drill'"
             [style.top.px]="drill.top" [style.left.px]="drill.left"
             (keydown.escape)="$event.stopPropagation(); closeDrill(true)">
          <p [id]="panelId + '-drill'" class="drill-title">
            {{ drill.selection.length ? 'Drill into' : 'This column' }}
          </p>
          @if (drill.selection.length) {
            <div class="drill-filters">
              @for (part of drill.selection; track $index; let last = $last) {
                <span class="drill-filter"><span class="drill-q">{{ questionName(part.variableKey) }}</span> {{ part.answer }}</span>
                @if (!last) { <span class="drill-and">and</span> }
              }
            </div>
            <p class="drill-n" [class.is-caution]="statusOf(drill.n) === 'caution'" [class.is-low]="statusOf(drill.n) === 'low'">
              <i class="fas mr-1" aria-hidden="true"
                 [class.fa-users]="statusOf(drill.n) === 'ok'"
                 [class.fa-triangle-exclamation]="statusOf(drill.n) !== 'ok'"></i>
              <strong>{{ drill.n }}</strong> respondents
              @if (statusOf(drill.n) === 'low') {
                -- too few to read; the minimum is {{ settings?.minBase }}.
              } @else if (statusOf(drill.n) === 'caution') {
                -- read with caution.
              }
            </p>
          }
          <div class="drill-actions">
            @if (drill.selection.length) {
              <button #drillPrimary type="button" class="xt-chip is-primary"
                      [disabled]="statusOf(drill.n) === 'low'"
                      (click)="commitDrill()">Drill in</button>
              @if (drill.siblingsOf) {
                <button type="button" class="xt-chip" (click)="commitSiblings()">
                  All {{ questionName(drill.siblingsOf) }}
                </button>
              }
            }
            @if (drill.sortColumn !== null) {
              <button #drillSort type="button" class="xt-chip" (click)="sortBy(drill.sortColumn)">
                <i class="fas fa-arrow-down-wide-short mr-1" aria-hidden="true"></i>{{ sortColumn === drill.sortColumn ? 'Unsort' : 'Sort rows by this' }}
              </button>
            }
            <button type="button" class="xt-chip is-quiet" (click)="closeDrill(true)">Cancel</button>
          </div>
        </div>
      }
    </div>

    <ng-template #tableTpl let-table let-mini="mini">
      <!-- Scrolls rather than squeezes: a banner of eight columns will not fit a half-width block at
           any font size worth reading. The answer column stays pinned while the figures scroll. -->
      <div class="table-scroll">
        <table class="data-table" [class.compact]="compact || mini" [class.is-drillable]="!!explore && !compact">
          <caption class="sr-only">{{ table.caption }}</caption>
          <thead>
            @if (table.banner.length) {
              <tr class="banner-row">
                <td class="corner sticky" [style.background]="headerFill"></td>
                @for (group of table.banner; track $index) {
                  <th scope="colgroup" [attr.colspan]="group.span"
                      [style.background]="headerFill" [style.color]="headerText">{{ group.label }}</th>
                }
              </tr>
            }
            <tr>
              <!-- Heads the column of answer labels, so it is a th with a column scope rather than
                   the blank corner cell a crosstab often has. -->
              <th scope="col" class="corner sticky"
                  [style.background]="headerFill" [style.color]="headerText">{{ cornerLabel }}</th>
              @for (column of table.columns; track $index; let c = $index) {
                <th scope="col" [class.total]="column.total" [class.is-hover]="hoverColumn === c && hoverTable === table"
                    [attr.aria-sort]="sortColumn === c ? 'descending' : null"
                    [style.background]="headerFill" [style.color]="headerText">
                  @if (explore && !compact) {
                    <button type="button" class="head-btn"
                            [attr.aria-label]="'Options for ' + column.name"
                            (click)="openColumn(table, c, $event)">
                      <span class="name">{{ column.name }}</span>
                      @if (sortColumn === c) { <i class="fas fa-arrow-down-wide-short ml-1" aria-hidden="true"></i> }
                    </button>
                  } @else {
                    <span class="name">{{ column.name }}</span>
                  }
                  @if (showLetters && column.letter) {
                    <span class="letter" [attr.aria-label]="'column ' + column.letter">{{ column.letter }}</span>
                  }
                </th>
              }
            </tr>
            <tr class="base-row">
              <th scope="row" class="row-head sticky">Base</th>
              @for (column of table.columns; track $index) {
                <td [class.total]="column.total" [class.low]="column.lowBase" [class.caution]="column.caution">
                  {{ column.base }}@if (column.lowBase) {<span class="flag" aria-hidden="true">*</span><span class="sr-only"> (too few respondents)</span>} @else if (column.caution) {<span class="flag" aria-hidden="true">!</span><span class="sr-only"> (read with caution)</span>}
                </td>
              }
            </tr>
            @if (weighted) {
              <tr class="base-row">
                <th scope="row" class="row-head sticky">Weighted base</th>
                @for (column of table.columns; track $index) {
                  <td [class.total]="column.total">{{ column.weightedBase }}</td>
                }
              </tr>
              <tr class="base-row">
                <th scope="row" class="row-head sticky">Effective base</th>
                @for (column of table.columns; track $index) {
                  <td [class.total]="column.total">{{ column.effectiveBase }}</td>
                }
              </tr>
            }
          </thead>
          <tbody (mouseleave)="hoverColumn = -1">
            @for (row of table.rows; track $index; let even = $even; let r = $index) {
              <!-- Banded off the deck's own colour rather than a fixed grey, so a table reads as part
                   of the report it sits in. Bound per row because the tint is computed from the
                   palette, which CSS cannot see. -->
              <tr [class.net]="row.kind === 'net'" [class.mean]="row.kind === 'mean'"
                  [class.first-summary]="r === table.firstSummary"
                  [style.--row-fill]="even ? 'var(--nav-true-white, #fff)' : rowFill">
                <th scope="row" class="row-head sticky">
                  @if (explore && !compact && row.answer) {
                    <button type="button" class="head-btn is-row" (click)="openRow(table, r, $event)">{{ row.label }}</button>
                  } @else {
                    {{ row.label }}
                  }
                </th>
                @for (cell of row.cells; track $index; let c = $index) {
                  <td [class.total]="table.columns[c].total"
                      [class.low]="table.columns[c].lowBase"
                      [class.is-hover]="hoverColumn === c && hoverTable === table"
                      [style.background]="cell.shade || null"
                      (mouseenter)="hoverColumn = c; hoverTable = table">
                    @if (explore && !compact && row.answer && !cell.suppressed && table.columns[c].selection.length) {
                      <button type="button" class="cell-btn" (click)="openCell(table, r, c, $event)"
                              data-cell
                              [attr.data-row]="r" [attr.data-col]="c"
                              [attr.tabindex]="isRovingStop(table, r, c) ? 0 : -1"
                              (focus)="rove = { table: table, row: r, col: c }"
                              (keydown)="moveCell($event, table, r, c)"
                              [attr.aria-label]="row.label + ', ' + table.columns[c].name + ': ' + cell.value + cell.spoken + '. Drill in.'">
                        <ng-container *ngTemplateOutlet="cellTpl; context: { $implicit: cell }"></ng-container>
                      </button>
                    } @else {
                      <ng-container *ngTemplateOutlet="cellTpl; context: { $implicit: cell }"></ng-container>
                      @if (cell.spoken) {
                        <span class="sr-only">{{ cell.spoken }}</span>
                      }
                    }
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
      @if (!mini && !compact && table.differences.length) {
        <div class="differences" role="note" aria-label="Key differences">
          <span class="differences-title"><i class="fas fa-wand-magic-sparkles mr-1" aria-hidden="true"></i>Key differences</span>
          <ul class="list-unstyled mb-0">
            @for (difference of table.differences; track difference) {
              <li>{{ difference }}</li>
            }
          </ul>
        </div>
      }
    </ng-template>

    <ng-template #cellTpl let-cell>
      @if (cell.suppressed) {
        <span class="suppressed" title="Too few respondents to show">··</span>
        <span class="sr-only">suppressed, too few respondents</span>
      } @else {
        <span class="cell-line">
          <span class="value" [style.color]="cell.ink || null">{{ cell.value }}</span>
          @if (cell.letters || cell.direction) {
            <!-- Tags rather than superscripts: a superscript reads as a footnote mark, and at that
                 size in an accent colour it was barely legible. -->
            <span class="sig-tags" aria-hidden="true">
              @if (cell.letters) {
                <span class="sig-tag is-letters" [title]="'Significantly higher than column' + (cell.beats.length > 1 ? 's ' : ' ') + cell.beats.join(', ')">{{ cell.letters }}</span>
              }
              @if (cell.direction) {
                <span class="sig-tag" [class.is-up]="cell.direction === 'higher'" [class.is-down]="cell.direction === 'lower'"
                      [title]="'Significantly ' + cell.direction + ' than everyone else'">{{ cell.direction === 'higher' ? '▲' : '▼' }}</span>
              }
            </span>
          }
        </span>
        @if (cell.count) {
          <span class="count" [style.color]="cell.ink || null">{{ cell.count }}</span>
        }
      }
    </ng-template>
  `,
  styles: [`
    :host { display: block; position: relative; }

    /* Above the table: a drill's trail on the left, the options toggle on the right. */
    .xt-top {
      display: flex; flex-wrap: wrap; align-items: center; gap: 6px 12px;
      margin-bottom: 8px; min-height: 24px;
    }
    .crumbs-label {
      font-size: var(--ecr-font-micro, 9px); font-weight: 700; letter-spacing: 0.08em;
      text-transform: uppercase; color: var(--ecr-mut, #5a6b73);
    }
    .crumb-reset {
      margin-left: 4px; padding: 0; border: 0; background: transparent;
      font-size: var(--ecr-font-xs, 11px); font-weight: 600; color: var(--ecr-primary, #13455b);
    }
    .crumb-reset:hover, .crumb-reset:focus-visible { text-decoration: underline; }
    .xt-toggle {
      display: inline-flex; align-items: center; gap: 6px; margin-left: auto;
      padding: 3px 10px; border: 1px solid transparent; border-radius: 999px;
      font-size: var(--ecr-font-xs, 11px); font-weight: 600;
      color: var(--ecr-mut, #5a6b73); background: transparent;
      transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
    }
    .xt-toggle:hover, .xt-toggle:focus-visible, .xt-toggle.is-open {
      color: var(--ecr-primary, #13455b); border-color: var(--ecr-line, #dbe4e8); background: var(--ecr-card, #fff);
    }
    .xt-count {
      padding: 0 6px; border-radius: 999px; font-weight: 600;
      color: var(--ecr-primary, #13455b); background: var(--ecr-primary-wash, #eef3f5);
    }
    .xt-chevron { font-size: 9px; transition: transform 0.15s ease; }
    .xt-toggle.is-open .xt-chevron { transform: rotate(180deg); }
    /* Everything that is not the table: what to look at, what the marks mean, what was tested. */
    .xt-panel {
      display: flex; flex-direction: column;
      margin-bottom: 12px;
      border: 1px solid var(--ecr-line, #dbe4e8); border-radius: var(--ecr-radius-sm, 6px);
      background: var(--ecr-bg, #f4f7f8);
      animation: panel-in 0.15s ease-out;
    }
    .xt-panel > * { padding: 12px 14px; }
    .xt-panel > * + * { border-top: 1px solid var(--ecr-line, #dbe4e8); }
    .xt-section { display: flex; flex-direction: column; gap: 8px; }
    .xt-section-title {
      margin: 0; font-size: var(--ecr-font-micro, 9px); font-weight: 700; letter-spacing: 0.08em;
      text-transform: uppercase; color: var(--ecr-mut, #5a6b73);
    }
    @keyframes panel-in { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: none; } }
    .xt-legends { display: flex; flex-wrap: wrap; gap: 4px 20px; }
    .crumbs { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; min-width: 0; }
    .crumb {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 3px 8px; border: 0; border-radius: 999px;
      font-size: var(--ecr-font-xs, 11px); font-weight: 600;
      color: var(--ecr-ink, #26414c); background: transparent;
    }
    .crumb:not(:disabled):hover { background: var(--ecr-primary-wash, #eef3f5); }
    .crumb:disabled { cursor: default; }
    .crumb[aria-current='step'] { color: var(--ecr-primary, #13455b); }
    .crumb-sep { font-size: 9px; color: var(--ecr-mut, #5a6b73); }
    .crumb-chip {
      display: inline-flex; align-items: center;
      border: 1px solid var(--ecr-line, #dbe4e8); border-radius: 999px; background: var(--ecr-card, #fff);
      animation: chip-in 0.18s ease-out;
    }
    .crumb-chip.is-current { border-color: var(--ecr-primary, #13455b); background: var(--ecr-primary-wash, #eef3f5); }
    .crumb-x {
      width: 20px; height: 20px; margin-right: 2px; padding: 0; border: 0; border-radius: 50%;
      font-size: 10px; color: var(--ecr-mut, #5a6b73); background: transparent;
    }
    .crumb-x:hover, .crumb-x:focus-visible { color: var(--ecr-negative, #c53030); background: rgba(0, 0, 0, 0.05); }
    .crumb-n {
      padding: 0 6px; border-radius: 999px; font-weight: 600; font-variant-numeric: tabular-nums;
      color: var(--ecr-mut, #5a6b73); background: var(--ecr-bg, #eef3f5);
    }
    .is-caution { color: #8a5a00 !important; }
    .crumb-n.is-caution, .slice-n.is-caution { background: #fff3d6; }
    .is-low { color: var(--ecr-negative, #c53030) !important; }
    .crumb-n.is-low, .slice-n.is-low { background: #fde8e8; }
    @keyframes chip-in { from { opacity: 0; transform: translateX(-4px); } to { opacity: 1; transform: none; } }

    /* Pickers share the width evenly and stack when the block is narrow; labels sit above. */
    .xt-picks { display: grid; grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr)); gap: 10px 12px; }
    .xt-pick { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
    .xt-pick-row { display: flex; align-items: center; gap: 6px; min-width: 0; }
    .xt-pick or-report-select { flex: 1 1 auto; min-width: 0; }
    .xt-pick-label { font-size: var(--ecr-font-xs, 11px); font-weight: 600; color: var(--ecr-ink, #26414c); }
    .xt-explore { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 6px 12px; }
    .xt-explore .xt-hint { margin: 0; }
    .xt-explore-actions { display: flex; flex-wrap: wrap; gap: 6px; }
    .xt-chip {
      display: inline-flex; align-items: center;
      padding: 3px 10px; border: 1px solid var(--ecr-line, #dbe4e8); border-radius: 999px;
      font-size: var(--ecr-font-xs, 11px); font-weight: 600;
      color: var(--ecr-ink, #26414c); background: var(--ecr-card, #fff);
      transition: background 0.12s ease, border-color 0.12s ease;
    }
    .xt-chip:hover:not(:disabled) { border-color: var(--ecr-primary, #13455b); }
    .xt-chip[aria-pressed='true'] { color: var(--ecr-primary, #13455b); background: var(--ecr-primary-wash, #eef3f5); border-color: var(--ecr-primary, #13455b); }
    .xt-chip.is-primary { color: var(--ecr-on-primary, #fff); background: var(--ecr-primary, #13455b); border-color: var(--ecr-primary, #13455b); }
    .xt-chip.is-primary:disabled { opacity: 0.45; }
    .xt-chip.is-quiet { border-color: transparent; background: transparent; }
    .segmented { display: inline-flex; border: 1px solid var(--ecr-line, #dbe4e8); border-radius: 999px; overflow: hidden; }
    .segmented button { padding: 3px 9px; border: 0; font-size: 11px; color: var(--ecr-mut, #5a6b73); background: var(--ecr-card, #fff); }
    .segmented button + button { border-left: 1px solid var(--ecr-line, #dbe4e8); }
    .segmented button[aria-pressed='true'] { color: var(--ecr-primary, #13455b); background: var(--ecr-primary-wash, #eef3f5); }
    .xt-hint { margin: -2px 0 8px; font-size: var(--ecr-font-xs, 11px); color: var(--ecr-mut, #5a6b73); }

    /* Slices as tabs: one table at a time. */
    .slice-tabs { display: flex; flex-wrap: wrap; gap: 2px; margin-bottom: 8px; border-bottom: 1px solid var(--ecr-line, #dbe4e8); }
    .slice-tabs button {
      margin-bottom: -1px; padding: 6px 12px; border: 0; border-bottom: 2px solid transparent;
      font-size: var(--ecr-font-sm, 12px); font-weight: 600; color: var(--ecr-mut, #5a6b73); background: transparent;
    }
    .slice-tabs button[aria-selected='true'] { color: var(--ecr-primary, #13455b); border-bottom-color: var(--ecr-primary, #13455b); }
    .tab-n { margin-left: 4px; font-weight: 400; font-size: 0.9em; opacity: 0.75; }

    /* Slices as a grid of small tables, the drilled one ringed. */
    .slice-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }
    .slice-card {
      min-width: 0; padding: 8px; border: 1px solid var(--ecr-line, #dbe4e8); border-radius: var(--ecr-radius-sm, 6px);
      background: var(--ecr-card, #fff); transition: box-shadow 0.15s ease, border-color 0.15s ease;
    }
    .slice-card.is-highlighted { border-color: var(--ecr-primary, #13455b); box-shadow: 0 0 0 2px var(--ecr-primary-wash, #eef3f5); }
    .slice-head { display: flex; align-items: baseline; gap: 8px; margin-bottom: 6px; }
    .slice-title {
      padding: 0; border: 0; background: transparent; text-align: left;
      font-size: var(--ecr-font-sm, 12px); font-weight: 700; color: var(--ecr-ink, #26414c);
    }
    button.slice-title:hover { color: var(--ecr-primary, #13455b); text-decoration: underline; }
    .slice-n { margin-left: auto; padding: 0 6px; border-radius: 999px; font-size: var(--ecr-font-xs, 11px); color: var(--ecr-mut, #5a6b73); }

    .table-scroll { overflow-x: auto; }

    .data-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      font-size: var(--ecr-font-base, 13px);
      font-variant-numeric: tabular-nums;
    }
    .data-table.compact { font-size: var(--ecr-font-xs, 11px); }

    /* Figures centre in their column; only the answer labels are left. */
    .data-table th,
    .data-table td {
      padding: 6px 10px;
      text-align: center;
      vertical-align: middle;
      border-bottom: 1px solid var(--ecr-line, #e6e6e6);
      white-space: nowrap;
    }
    .data-table.compact th, .data-table.compact td { padding: 4px 6px; }

    /* The answer labels are sentences; everything else is a figure.
       Qualified with .data-table on purpose: the rule above it is ".data-table th", a class plus a
       type, which outranks a bare ".row-head". */
    .data-table .corner,
    .data-table .row-head {
      text-align: left;
      white-space: normal;
      min-width: 10rem;
      /* A long answer wraps rather than taking the table: the figures are what is being read. */
      max-width: min(40%, 22rem);
      font-weight: 400;
    }
    .data-table.compact .corner, .data-table.compact .row-head { min-width: 6rem; }

    /* Pinned while the figures scroll sideways. Its fill is the row's own, so nothing shows through. */
    .data-table .sticky { position: sticky; left: 0; z-index: 1; }
    tbody .sticky { background: var(--row-fill, var(--ecr-card, #fff)); }
    .base-row .sticky { background: var(--ecr-bg, #f4f7f8); }
    .xt:focus { outline: none; }
    tbody tr { background: var(--row-fill, transparent); }
    /* The crosshair: the row under the pointer and its column, so a figure is read against both. */
    tbody tr:hover > *, tbody td.is-hover { box-shadow: inset 0 0 0 9999px rgba(0, 0, 0, 0.035); }
    thead th.is-hover { box-shadow: inset 0 -3px 0 rgba(255, 255, 255, 0.55); }

    thead th {
      font-weight: 600;
      border-bottom: 0;
      vertical-align: bottom;
    }
    thead tr:first-child > :first-child { border-top-left-radius: var(--ecr-radius-sm, 4px); }
    thead tr:first-child > :last-child { border-top-right-radius: var(--ecr-radius-sm, 4px); }
    .banner-row th {
      font-size: var(--ecr-font-xs, 11px);
      font-weight: 600;
      letter-spacing: 0.02em;
      border-bottom: 1px solid rgba(255, 255, 255, 0.35);
    }

    thead .name { white-space: normal; }
    .letter {
      display: inline-block;
      margin-left: 6px;
      min-width: 1.4em;
      padding: 0 4px;
      border-radius: 999px;
      font-size: 0.75em;
      line-height: 1.5;
      font-weight: 600;
      background: rgba(255, 255, 255, 0.22);
    }

    /* Headings and cells that drill: buttons that look like the text they replace until hovered. */
    .head-btn, .cell-btn {
      padding: 0; border: 0; background: transparent; color: inherit; font: inherit; text-align: inherit;
    }
    .head-btn { border-bottom: 1px dashed transparent; }
    .head-btn:hover, .head-btn:focus-visible { border-bottom-color: currentColor; }
    .head-btn.is-row { text-align: left; }
    .cell-btn { display: block; width: 100%; border-radius: 4px; }
    .cell-btn:hover, .cell-btn:focus-visible { outline: 2px solid var(--ecr-primary, #13455b); outline-offset: 2px; }
    .cell-btn { cursor: pointer; }

    .base-row th,
    .base-row td {
      font-size: var(--ecr-font-xs, 11px);
      color: var(--ecr-mut, #5a6b73);
      background: var(--ecr-bg, #f4f7f8);
      padding-top: 4px;
      padding-bottom: 4px;
    }
    .base-row td.low { color: var(--ecr-negative, #b3261e); font-weight: 600; }
    .base-row td.caution { color: #8a5a00; font-weight: 600; }
    .flag { margin-left: 1px; }

    /* A rule between every column, so a figure is read against its own heading rather than
       drifting into the column beside it. */
    .data-table th:not(:first-child),
    .data-table td:not(:first-child) { border-left: 1px solid var(--ecr-line, #e6e6e6); }
    .data-table thead th:not(:first-child) { border-left: 1px solid rgba(255, 255, 255, 0.35); }

    /* The column every other column is read against. Its weight is what sets it apart. */
    .total { font-weight: 600; }

    /* Too few respondents to read: the figures stay, quieter, so nobody quotes them by accident. */
    /* Readable, but plainly not to be quoted: muted and italic rather than faded to near-nothing. */
    tbody td.low .value { color: var(--ecr-mut, #5a6b73); font-style: italic; font-weight: 400; }
    .suppressed { letter-spacing: 0.1em; color: var(--ecr-mut, #5a6b73); }

    /* Nets and the mean summarise the answers above them, so a rule sets them apart. */
    tbody tr.first-summary > * { border-top: 2px solid var(--ecr-line, #dbe4e8); }
    tbody tr.net .row-head,
    tbody tr.mean .row-head { font-weight: 600; }
    tbody tr.mean .row-head { font-style: italic; }

    tbody tr:last-child th,
    tbody tr:last-child td { border-bottom: 0; }

    /* The figure and its tags on one line, the figure first so columns of figures still align. */
    .cell-line { display: inline-flex; align-items: center; gap: 4px; }
    .value { font-weight: 500; color: var(--ecr-ink, #26414c); }

    /* Significance as tags: the deck's ink on a pale tint of what the mark means -- the accent for
       a column letter, positive and negative for above and below everyone. The tint is pale enough
       that the ink holds its contrast on any theme, and the shape, not only the colour, says it. */
    .sig-tags { display: inline-flex; gap: 2px; }
    .sig-tag {
      display: inline-flex; align-items: center; justify-content: center;
      min-width: 18px; height: 16px; padding: 0 4px; border-radius: 4px;
      font-size: 10px; font-weight: 700; line-height: 1; letter-spacing: 0.03em;
      color: var(--ecr-ink, #26414c);
      border: 1px solid transparent;
    }
    .sig-tag.is-letters { background: var(--sig-letters-fill, #fde8d7); border-color: var(--sig-letters-line, #f3c3a0); }
    .sig-tag.is-up { background: var(--sig-up-fill, #dcf2e3); border-color: var(--sig-up-line, #a8d9b8); }
    .sig-tag.is-down { background: var(--sig-down-fill, #fbe0e0); border-color: var(--sig-down-line, #f0b0b0); }
    .data-table.compact .sig-tag { min-width: 15px; height: 14px; font-size: 9px; }

    .count { display: block; font-size: 0.85em; color: var(--ecr-mut, #5a6b73); }

    /* The widest significant gaps, in words, between the table and its footnotes. */
    .differences {
      display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 12px;
      margin-top: 8px; padding: 8px 10px; border-radius: var(--ecr-radius-sm, 6px);
      font-size: var(--ecr-font-sm, 12px); color: var(--ecr-ink, #26414c);
      background: var(--ecr-primary-wash, #eef3f5);
    }
    .differences-title {
      font-size: var(--ecr-font-micro, 9px); font-weight: 700; letter-spacing: 0.08em;
      text-transform: uppercase; color: var(--ecr-primary, #13455b);
    }
    .differences ul { display: flex; flex-wrap: wrap; gap: 4px 16px; }
    .differences li::before { content: '•'; margin-right: 6px; color: var(--ecr-accent, #c05621); }

    .legend {
      display: flex; align-items: center; gap: 6px; max-width: 22rem; margin-bottom: 4px;
      font-size: var(--ecr-font-xs, 11px); color: var(--ecr-mut, #5a6b73);
    }
    .legend-ramp { flex: 0 0 8rem; height: 8px; border-radius: 999px; }
    .legend-key { display: inline-block; width: 10px; height: 10px; border-radius: 2px; }

    .footnotes {
      margin: 0; padding-left: 1rem;
      font-size: var(--ecr-font-xs, 11px); line-height: 1.45;
      color: var(--ecr-mut, #5a6b73);
    }
    .footnotes li + li { margin-top: 3px; }
    .foot-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 6px; }
    .xt-panel > .foot-actions { padding-top: 8px; padding-bottom: 8px; background: var(--ecr-card, #fff); border-radius: 0 0 var(--ecr-radius-sm, 6px) var(--ecr-radius-sm, 6px); }
    .download {
      font-size: var(--ecr-font-xs, 11px);
      color: var(--ecr-mut, #5a6b73);
      white-space: nowrap;
    }
    .download:hover, .download:focus-visible { color: var(--ecr-ink, #26414c); }

    /* What a click is about to do, and how many respondents it leaves, before it does it. */
    .drill-pop {
      position: absolute; z-index: 20; width: 17rem; padding: 12px;
      border: 1px solid var(--ecr-line, #dbe4e8); border-radius: var(--ecr-radius, 8px);
      background: var(--ecr-card, #fff); box-shadow: 0 12px 32px rgba(16, 32, 48, 0.18);
      animation: pop-in 0.14s ease-out;
    }
    @keyframes pop-in { from { opacity: 0; transform: translateY(-4px) scale(0.98); } to { opacity: 1; transform: none; } }
    .drill-title {
      margin: 0 0 6px; font-size: var(--ecr-font-micro, 9px); font-weight: 700;
      letter-spacing: 0.08em; text-transform: uppercase; color: var(--ecr-mut, #5a6b73);
    }
    .drill-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; margin-bottom: 8px; }
    /* One box with a modest radius rather than an inline pill, so a long answer wraps cleanly. */
    .drill-filter {
      display: inline-block; max-width: 100%; overflow-wrap: anywhere; line-height: 1.4;
      padding: 2px 8px; border-radius: 10px; font-size: var(--ecr-font-xs, 11px); font-weight: 600;
      color: var(--ecr-primary, #13455b); background: var(--ecr-primary-wash, #eef3f5);
    }
    .drill-q { font-weight: 400; opacity: 0.75; }
    .drill-q::after { content: ':'; }
    .drill-and { font-size: var(--ecr-font-xs, 11px); color: var(--ecr-mut, #5a6b73); }
    .drill-n { margin: 0 0 10px; font-size: var(--ecr-font-sm, 12px); color: var(--ecr-ink, #26414c); }
    .drill-actions { display: flex; flex-wrap: wrap; gap: 6px; }
    .drill-actions .xt-chip { max-width: 100%; border-radius: 10px; text-align: left; overflow-wrap: anywhere; line-height: 1.4; }

    @media (prefers-reduced-motion: reduce) {
      .crumb-chip, .drill-pop, .xt-panel { animation: none; }
    }
  `],
})
export class DataTableBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() compact?: boolean;
  @Input() explore?: ExploreHandle;

  @ViewChild('drillPrimary') private drillPrimary?: ElementRef<HTMLButtonElement>;
  @ViewChild('drillSort') private drillSort?: ElementRef<HTMLButtonElement>;
  @ViewChild('popover') private popover?: ElementRef<HTMLElement>;
  @ViewChild('region') private region?: ElementRef<HTMLElement>;

  /** Read out after a change the reader made, so the table changing under them is said. */
  announcement = '';
  /** Set when a change should bring focus back to the table once it has been redrawn. */
  private refocus = false;
  /** The figure the table's single tab stop is on. */
  rove: { table: TableModel; row: number; col: number } | null = null;
  /** The click that opened the popover, which the document listener must not close it for. */
  private openingClick: Event | null = null;

  constructor(private host: ElementRef<HTMLElement>) { }

  tables: TableModel[] = [];
  caption = '';
  cornerLabel = 'Answer';
  footnotes: string[] = [];
  headerFill = '';
  headerText = '';
  /** The banded row's background: the palette's primary, almost all the way to white. */
  rowFill = '';
  upFill = '';
  downFill = '';
  heatRamp = '';
  weighted = false;
  showLetters = false;
  settings: CrosstabTableSettings | null = null;
  legend: 'heat' | 'significance' | null = null;
  view: 'single' | 'nested' | 'tabs' | 'grid' = 'single';
  activeSlice = 0;
  hoverColumn = -1;
  hoverTable: TableModel | null = null;
  /** A column the reader has sorted the rows by, in this view only. */
  sortColumn: number | null = null;
  pending: PendingDrill | null = null;
  copied = false;
  /** Table options open: the pickers, the notes and the exports. Shut by default -- the table is the view. */
  expanded = false;

  readonly panelId = `xt-${Math.random().toString(36).slice(2, 9)}`;
  readonly views: { value: 'nested' | 'tabs' | 'grid'; text: string; hint: string; icon: string }[] = [
    { value: 'nested', text: 'Nested columns', hint: 'Nested -- every slice in one table', icon: 'fa-table-columns' },
    { value: 'tabs', text: 'Tabs', hint: 'Tabs -- one slice at a time', icon: 'fa-folder' },
    { value: 'grid', text: 'Grid', hint: 'Grid -- small tables side by side', icon: 'fa-grip' },
  ];

  ngOnChanges() {
    this.build();
    if (this.refocus) {
      this.refocus = false;
      // After a drill, a step back or a reset, the control that was used has gone; the table is
      // where the reader is, so that is where focus goes.
      setTimeout(() => this.region?.nativeElement.focus());
    }
  }

  /**
   * Keys used inside the table stay inside it. The deck listens on the document -- arrows turn the
   * page while presenting, Escape leaves -- and an arrow in the Rows search or between slice tabs
   * was turning the slide.
   */
  guardKeys(event: KeyboardEvent) {
    if (event.target === this.region?.nativeElement) return;
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Escape', 'Home', 'End'].includes(event.key)) {
      event.stopPropagation();
    }
  }

  isRovingStop(table: TableModel, row: number, col: number): boolean {
    const at = this.rove;
    if (!at || at.table !== table) return row === this.firstDrillRow(table) && col === 0;
    return at.row === row && at.col === col;
  }

  private firstDrillRow(table: TableModel): number {
    return Math.max(0, table.rows.findIndex(row => !!row.answer));
  }

  /** Arrow keys between the figures, as a spreadsheet: one tab stop for the whole grid. */
  moveCell(event: KeyboardEvent, table: TableModel, row: number, col: number) {
    const step: Record<string, [number, number]> = {
      ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1],
    };
    const move = step[event.key];
    if (!move || event.altKey) return;
    event.preventDefault();
    const grid = (event.currentTarget as HTMLElement).closest('table');
    const target = grid?.querySelector<HTMLButtonElement>(
      `[data-cell][data-row="${row + move[0]}"][data-col="${col + move[1]}"]`);
    target?.focus();
  }

  private say(text: string) {
    this.announcement = '';
    setTimeout(() => { this.announcement = text; });
  }

  /** The reader's change, made, with focus and an announcement to follow the redraw. */
  private change(next: CrosstabExplore | null, said: string) {
    if (!this.explore) return;
    this.refocus = true;
    this.say(said);
    this.explore.change(next);
  }

  get state(): CrosstabExplore | undefined {
    return this.explore?.state;
  }

  get path() {
    return this.state?.path ?? [];
  }

  get pristine(): boolean {
    return !this.state;
  }

  get siblings(): boolean {
    return !!this.state?.siblings;
  }

  /**
   * Everyone in the table before the first drill. Read off the table rather than off a Total column,
   * which is one slice's in tabs or a grid, and absent from a table drawn without one.
   */
  get rootN(): number | null {
    if (this.path.length) return this.state?.rootN ?? null;
    return this.settings?.respondents ?? null;
  }

  get sliced(): boolean {
    return this.view !== 'single';
  }

  get shownTables(): TableModel[] {
    return this.tables.slice(0, GRID_LIMIT);
  }

  get crosstab() {
    return this.block.reportType === 'crosstab' ? this.block.reportExtraData : null;
  }

  get rowKey(): string {
    return this.crosstab?.variableKey ?? '';
  }

  get columnKey(): string {
    const extra = this.crosstab;
    return extra?.subgroups?.length ? extra.subgroups[0].variableKey : extra?.groupBy ?? '';
  }

  get layerKey(): string {
    return this.crosstab?.layerBy ?? '';
  }

  get layerName(): string {
    return this.questionName(this.layerKey);
  }

  private optionOf(key: string): ReportSelectOption | null {
    const question = this.explore?.questions.find(candidate => candidate.key === key);
    return question ? { value: question.key, code: question.code, text: question.label } : null;
  }

  get rowOptions(): ReportSelectOption[] {
    return (this.explore?.questions ?? []).map(question => this.optionOf(question.key)!);
  }

  get columnOptions(): ReportSelectOption[] {
    return [
      { value: TOTAL_ONLY, text: 'Total only' },
      ...(this.explore?.questions ?? [])
        .filter(question => question.splits && question.key !== this.rowKey)
        .map(question => this.optionOf(question.key)!),
    ];
  }

  /** What the Columns picker shows: the question, or "Total only" where the table has no columns. */
  get columnValue(): string {
    return this.columnKey || (this.state?.columnKey === null ? TOTAL_ONLY : '');
  }

  get splitOptions(): ReportSelectOption[] {
    return [
      { value: '', text: 'None' },
      ...(this.explore?.questions ?? [])
        .filter(question => question.splits && question.key !== this.rowKey && question.key !== this.columnKey)
        .map(question => this.optionOf(question.key)!),
    ];
  }

  questionName(key: string): string {
    const question = this.explore?.questions.find(candidate => candidate.key === key);
    return question ? question.label : key;
  }

  /** Where a base sits against the report's thresholds -- counted in respondents, never weighted. */
  statusOf(n: number): 'ok' | 'caution' | 'low' {
    const settings = this.settings;
    if (!settings) return 'ok';
    if (n < settings.minBase) return 'low';
    if (n < settings.cautionBase) return 'caution';
    return 'ok';
  }

  private build() {
    if (this.block.reportType !== 'crosstab') return;
    const extra = this.block.reportExtraData;

    this.headerFill = this.palette.primary;
    this.headerText = textOn(this.palette.primary);
    // Far enough toward white that the row is a wash rather than a colour: the figures on it are
    // the content, and a band that competes with them is worse than no band at all.
    this.rowFill = tint(this.palette.primary, ROW_TINT);
    this.upFill = tint(this.palette.positive, SIGNIFICANCE_TINT);
    this.downFill = tint(this.palette.negative, SIGNIFICANCE_TINT);
    this.heatRamp = `linear-gradient(90deg, ${tint(this.palette.primary, HEAT_PALEST)}, ${tint(this.palette.primary, HEAT_STRONGEST)})`;
    const style = this.host.nativeElement.style;
    style.setProperty('--sig-letters-fill', tint(this.palette.accent, TAG_FILL));
    style.setProperty('--sig-letters-line', tint(this.palette.accent, TAG_LINE));
    style.setProperty('--sig-up-fill', tint(this.palette.positive, TAG_FILL));
    style.setProperty('--sig-up-line', tint(this.palette.positive, TAG_LINE));
    style.setProperty('--sig-down-fill', tint(this.palette.negative, TAG_FILL));
    style.setProperty('--sig-down-line', tint(this.palette.negative, TAG_LINE));

    // Only the columns that resolved. An unknown variable takes its own column down rather than the
    // table with it; the host says so when every one of them is unknown.
    const drawn = this.series.filter(s => s.known && s.crosstab);
    this.settings = drawn[0]?.crosstab?.table ?? null;
    this.weighted = !!this.settings?.weighted;
    this.showLetters = extra.sigTest === 'columns';
    this.legend = extra.shading === 'heat' ? 'heat' : extra.shading === 'significance' ? 'significance' : null;
    const layerView = this.settings?.layerView ?? null;
    this.view = layerView ?? 'single';

    // One table per slice for tabs and a grid; everything in one for a nested or plain table.
    const groups: ChartSeries[][] = [];
    if (layerView === 'tabs' || layerView === 'grid') {
      for (const s of drawn) {
        const last = groups[groups.length - 1];
        if (last && last[0].crosstab!.slice?.label === s.crosstab!.slice?.label) last.push(s);
        else groups.push([s]);
      }
    } else if (drawn.length) {
      groups.push(drawn);
    }

    const numeric = drawn.some(s => s.numeric);
    this.cornerLabel = numeric ? 'Item' : 'Answer';
    const siblingAnswer = this.siblings ? this.path[this.path.length - 1]?.answer : undefined;
    this.tables = groups.map(group => this.tableOf(group, numeric, siblingAnswer));
    if (this.activeSlice >= this.tables.length) this.activeSlice = 0;
    if (siblingAnswer && layerView === 'tabs') {
      this.activeSlice = Math.max(0, this.tables.findIndex(table => table.highlighted));
    }

    this.caption = this.block.header
      || `${this.tables[0]?.rows.length ?? 0} answers across ${this.tables[0]?.columns.length ?? 0} subgroups`;
    this.footnotes = this.notes(drawn, numeric);
    this.pending = null;
  }

  private tableOf(group: ChartSeries[], numeric: boolean, siblingAnswer?: string): TableModel {
    const extra = this.crosstab!;
    const settings = this.settings;
    const decimals = settings?.decimals ?? 0;
    const minCell = settings?.minCellCount ?? 0;
    const stats = group.map(s => s.crosstab!);
    const asCount = extra.cellValue === 'count';
    // Counts under a mean would be the same n in every row of the column -- the column's base,
    // which the header already carries.
    const withCounts = !!extra.showCounts && !asCount && !numeric;
    const drillable = !numeric;

    const columns: TableColumn[] = group.map((s, index) => ({
      name: s.name,
      base: s.crosstab!.sampleBase,
      weightedBase: s.base,
      effectiveBase: Math.round(s.crosstab!.effectiveBase),
      total: s.crosstab!.total ?? (extra.includeTotal !== false && index === 0),
      letter: s.crosstab!.letter,
      lowBase: s.crosstab!.lowBase,
      caution: s.crosstab!.caution,
      selection: s.crosstab!.selection,
    }));

    const spine = group.find(s => s.points.length)?.points ?? [];
    const percentText = (percent: number) => `${percent.toFixed(decimals)}%`;
    let rows: TableRow[] = spine.map((point, rowIndex) => ({
      label: point.label,
      kind: 'answer',
      answer: drillable && point.optionIndex >= 0 ? point.label : undefined,
      cells: group.map((column, c) => {
        const cell = column.points[rowIndex];
        const count = stats[c].counts[rowIndex] ?? 0;
        const value = !cell ? '—'
          : asCount ? String(Math.round(cell.value))
            : numeric ? cell.percent.toFixed(1)
              : percentText(cell.percent);
        return this.cell(value, withCounts && cell ? `(${Math.round(cell.value)})` : '',
          stats[c].marks[rowIndex], count, cell?.percent ?? 0, !!minCell && count > 0 && count < minCell);
      }),
    }));

    const netRows: TableRow[] = (extra.nets ?? [])
      .filter(net => net.label?.trim() && net.answers?.length)
      .map((net, n) => ({
        label: net.label,
        kind: 'net' as const,
        cells: stats.map(column => {
          const figure = column.nets[n];
          if (!figure) return this.cell('—', '', undefined, 0, 0, false);
          return this.cell(
            asCount ? String(figure.value) : percentText(figure.percent),
            withCounts ? `(${figure.value})` : '',
            figure.mark, figure.count, figure.percent,
            !!minCell && figure.count > 0 && figure.count < minCell);
        }),
      }));

    const meanRow: TableRow[] = extra.showMean && stats.some(column => column.mean)
      ? [{
        label: 'Mean',
        kind: 'mean',
        cells: stats.map(column => column.mean
          ? this.cell(column.mean.value.toFixed(2), '', column.mean.mark, column.mean.count, column.mean.value,
            !!minCell && column.mean.count < minCell)
          : this.cell('—', '', undefined, 0, 0, false)),
      }]
      : [];

    // A letter naming a cell that is not shown would point at a figure the reader cannot see.
    for (const row of [...rows, ...netRows]) {
      for (const cell of row.cells) {
        const shown = cell.beats.filter(letter =>
          !row.cells[columns.findIndex(column => column.letter === letter)]?.suppressed);
        if (shown.length === cell.beats.length) continue;
        cell.beats = shown;
        cell.letters = shown.join('');
      }
    }

    // Shaded in the order the marks were worked out in -- answers, nets, mean -- and only then sorted,
    // so a cell takes its colour with it when its row moves.
    if (extra.shading === 'heat' && !asCount) this.heat(rows, numeric);
    if (extra.shading === 'significance') this.significance([...rows, ...netRows, ...meanRow], stats);

    if (this.sortColumn !== null && this.sortColumn < columns.length) {
      const by = this.sortColumn;
      rows = [...rows].sort((a, b) => b.cells[by].sortValue - a.cells[by].sortValue);
    }
    const all = [...rows, ...netRows, ...meanRow];

    const slice = stats[0]?.slice && (this.view === 'tabs' || this.view === 'grid')
      ? {
        label: stats[0].slice.label,
        n: stats[0].slice.sampleBase,
        selection: [{ variableKey: stats[0].slice.variableKey, answer: stats[0].slice.label }],
      }
      : null;

    return {
      slice,
      columns,
      rows: all,
      banner: this.bannerOf(stats),
      firstSummary: netRows.length || meanRow.length ? rows.length : -1,
      caption: [this.block.header, slice?.label].filter(Boolean).join(' — ')
        || `${rows.length} answers across ${columns.length} subgroups`,
      highlighted: !!siblingAnswer && slice?.label === siblingAnswer,
      differences: this.differencesIn(all, columns),
    };
  }

  /**
   * The few significant differences most worth reading first, said in words: the widest gaps the
   * tests marked. Read off the marks rather than worked out again, so the words can only ever repeat
   * what the letters and arrows already say -- and nothing is said about a table that was not tested.
   */
  private differencesIn(rows: TableRow[], columns: TableColumn[]): string[] {
    const test = this.crosstab?.sigTest;
    if (test !== 'columns' && test !== 'total') return [];
    const total = columns.findIndex(column => column.total);
    const found: { gap: number; row: string; text: string }[] = [];

    rows.forEach(row => row.cells.forEach((cell, c) => {
      if (cell.suppressed || columns[c].total) return;
      if (test === 'columns') {
        for (const letter of cell.beats) {
          const other = columns.findIndex(column => column.letter === letter);
          if (other < 0) continue;
          const against = row.cells[other];
          if (against.suppressed) continue;
          found.push({
            gap: cell.sortValue - against.sortValue,
            row: row.label,
            text: `${columns[c].name} ${cell.value} vs ${columns[other].name} ${against.value}: ${row.label}`,
          });
        }
      } else if (cell.direction && total >= 0 && !row.cells[total].suppressed) {
        const everyone = row.cells[total];
        found.push({
          gap: Math.abs(cell.sortValue - everyone.sortValue),
          row: row.label,
          text: `${columns[c].name} ${cell.direction === 'higher' ? 'above' : 'below'} everyone on ${row.label}: ${cell.value} vs ${everyone.value}`,
        });
      }
    }));

    // One per row, so three differences are three findings rather than one answer three ways.
    const seen = new Set<string>();
    return found
      .sort((a, b) => b.gap - a.gap)
      .filter(item => !seen.has(item.row) && !!seen.add(item.row))
      .slice(0, MAX_DIFFERENCES)
      .map(item => item.text);
  }

  private cell(
    value: string, count: string, mark: CrosstabMark | undefined, n: number, sortValue: number, suppressed: boolean,
  ): TableCell {
    const letters = mark?.higherThan.join('') ?? '';
    const direction = mark?.versusRest ?? null;
    const spoken = [
      letters ? `significantly higher than column${letters.length > 1 ? 's' : ''} ${mark!.higherThan.join(', ')}` : '',
      direction ? `significantly ${direction} than everyone else` : '',
    ].filter(Boolean).join('; ');
    return {
      value, count, letters, beats: mark?.higherThan ?? [], direction, spoken: spoken ? ` (${spoken})` : '',
      shade: '', ink: '', suppressed, n, sortValue,
    };
  }

  /**
   * Heat shading over the answer rows, scaled to the largest figure in the table rather than to 100:
   * on a question where nothing passes 30%, shading against 100 is a table of near-white cells.
   */
  private heat(rows: TableRow[], numeric: boolean) {
    const figures = rows.flatMap(row => row.cells.filter(cell => !cell.suppressed).map(cell => cell.sortValue));
    if (!figures.length) return;
    const low = numeric ? Math.min(...figures) : 0;
    const high = Math.max(...figures);
    if (!(high > low)) return;

    for (const row of rows) {
      for (const cell of row.cells) {
        if (cell.suppressed || cell.value === '—') continue;
        const strength = (cell.sortValue - low) / (high - low);
        cell.shade = tint(this.palette.primary, HEAT_PALEST - strength * (HEAT_PALEST - HEAT_STRONGEST));
        cell.ink = this.inkOn(cell.shade);
      }
    }
  }

  /**
   * Green where a figure is significantly above everyone else's, red below. The arrow goes in too,
   * whatever test marks the cells, so the colour is never the only signal.
   */
  private significance(rows: TableRow[], stats: CrosstabColumn[]) {
    rows.forEach((row, r) => row.cells.forEach((cell, c) => {
      const shade = stats[c]?.shades[r];
      if (!shade || cell.suppressed) return;
      cell.shade = shade === 'higher' ? this.upFill : this.downFill;
      cell.ink = this.inkOn(cell.shade);
      if (!cell.direction) {
        cell.direction = shade;
        cell.spoken = `${cell.spoken} (significantly ${shade} than everyone else)`;
      }
    }));
  }

  /** Whichever of the deck's two text colours reads better on a shaded cell. */
  private inkOn(fill: string): string {
    return readableInk(fill, this.palette.darkText ?? DEFAULT_TEXT_COLORS.darkText,
      this.palette.lightText ?? DEFAULT_TEXT_COLORS.lightText);
  }

  /**
   * A header row naming the question each run of columns comes from -- only when there is more than
   * one, since a table split by one question says so in its own column names.
   */
  private bannerOf(stats: CrosstabColumn[]): BannerGroup[] {
    if (!stats.some(column => column.questionLabel)) return [];
    const groups: BannerGroup[] = [];
    let current = '';
    stats.forEach((column, i) => {
      const label = column.total ? '' : column.questionLabel ?? '';
      if (i > 0 && label === current) groups[groups.length - 1].span++;
      else groups.push({ label, span: 1 });
      current = label;
    });
    return groups;
  }

  /** Said plainly rather than left to be inferred: what was tested, weighted and flagged. */
  private notes(drawn: ChartSeries[], numeric: boolean): string[] {
    const extra = this.crosstab;
    const settings = this.settings;
    const notes: string[] = [];
    if (settings?.filteredTo !== null && settings?.filteredTo !== undefined && !this.path.length) {
      const answers = (extra?.tableFilter ?? []).map(entry => entry.answer).join(', ');
      notes.push(`Only respondents who answered ${answers} (${settings.filteredTo}).`);
    }
    // Hidden cells are still counted in the tests: the figure is withheld, not the respondents.
    if (!numeric && drawn.some(s => s.multiResponse)) {
      notes.push('Respondents could give more than one answer, so columns total more than 100%.');
    }
    if (settings?.weighted) {
      notes.push('Figures are weighted. Bases are respondents; the effective base is what the weighted sample is worth in tests.');
    }
    if (settings && extra?.sigTest === 'columns') {
      notes.push(`Letters mark a figure significantly higher than that column's, at ${settings.confidenceLevel}% confidence, ${CORRECTION_NAMES[settings.correction]}. Columns are compared within the same question.`);
    } else if (settings && (extra?.sigTest === 'total' || extra?.shading === 'significance')) {
      notes.push(`▲▼ mark a figure significantly higher or lower than everyone else's, at ${settings.confidenceLevel}% confidence, ${CORRECTION_NAMES[settings.correction]}.`);
    }
    if (settings?.overlapping.length && extra?.sigTest === 'columns') {
      notes.push(`${settings.overlapping.join(', ')}: respondents can be in more than one of these columns, so they are not tested against each other.`);
    }
    const columns = this.tables.flatMap(table => table.columns);
    if (settings && columns.some(column => column.lowBase)) {
      notes.push(`* Fewer than ${settings.minBase} respondents: too few to read${extra?.sigTest && extra.sigTest !== 'none' ? ', and not tested' : ''}.`);
    }
    if (settings && columns.some(column => column.caution)) {
      notes.push(`! Fewer than ${settings.cautionBase} respondents: read with caution.`);
    }
    if (settings?.minCellCount && this.tables.some(table => table.rows.some(row => row.cells.some(cell => cell.suppressed)))) {
      notes.push(`·· Fewer than ${settings.minCellCount} respondents in the cell, so it is not shown.`);
    }
    if (extra?.showMean && extra.meanExcludes?.length) {
      notes.push(`Mean excludes ${extra.meanExcludes.join(', ')}.`);
    }
    const widest = Math.max(0, ...this.tables.map(table => table.columns.length));
    if (this.view === 'nested' && widest > NESTED_COLUMN_WARNING) {
      notes.push(`${widest} columns: tabs or a grid will read more easily.`);
    }
    return notes;
  }

  // ---- drilling ----

  openColumn(table: TableModel, c: number, event: MouseEvent) {
    const column = table.columns[c];
    const selection = column.selection.length ? column.selection : table.slice?.selection ?? [];
    this.openDrill(event, {
      selection,
      n: column.base,
      siblingsOf: selection.length === 1 ? selection[0].variableKey : null,
      sortColumn: c,
    });
  }

  openRow(table: TableModel, r: number, event: MouseEvent) {
    const row = table.rows[r];
    if (!row.answer) return;
    const total = table.columns.findIndex(column => column.total);
    const selection = [...(table.slice?.selection ?? []), { variableKey: this.rowKey, answer: row.answer }];
    this.openDrill(event, {
      selection,
      // The Total's count is everyone in the table who gave the answer. Without a Total there is no
      // single column that holds them all, so the widest column stands in -- and the drill shows it.
      n: total >= 0 ? row.cells[total].n : Math.max(0, ...row.cells.map(cell => cell.n)),
      siblingsOf: table.slice ? null : this.rowKey,
      sortColumn: null,
    });
  }

  openCell(table: TableModel, r: number, c: number, event: MouseEvent) {
    const row = table.rows[r];
    const column = table.columns[c];
    if (!row.answer) return;
    this.openDrill(event, {
      selection: [...column.selection, { variableKey: this.rowKey, answer: row.answer }],
      n: row.cells[c].n,
      siblingsOf: null,
      sortColumn: null,
    });
  }

  private openDrill(event: MouseEvent, drill: Omit<PendingDrill, 'top' | 'left' | 'trigger'>) {
    // Not stopped: other tables' popovers close on this same click. The document listener below
    // only has to know not to close the one it opens.
    this.openingClick = event;
    const trigger = event.currentTarget as HTMLElement | null;
    const hostBox = this.host.nativeElement.getBoundingClientRect();
    const box = trigger?.getBoundingClientRect();
    const width = 17 * 16;
    const left = box ? Math.min(Math.max(0, box.left - hostBox.left), Math.max(0, hostBox.width - width)) : 0;
    const top = box ? box.bottom - hostBox.top + 6 : 0;
    this.pending = { ...drill, top, left, trigger };
    // Focus into the popover once it is drawn, so a keyboard reader lands on its first action.
    // The first action that can be taken -- Drill in is disabled under the minimum base -- or the
    // popover itself, so a keyboard reader always lands inside it and Escape always works.
    setTimeout(() => {
      const first = [this.drillPrimary, this.drillSort]
        .map(ref => ref?.nativeElement)
        .find(button => !!button && !button.disabled);
      (first ?? this.popover?.nativeElement)?.focus();
    });
  }

  closeDrill(returnFocus = false) {
    const trigger = this.pending?.trigger;
    this.pending = null;
    if (returnFocus) trigger?.focus();
  }

  /** A click anywhere outside the popover closes it, as a menu would. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.pending || event === this.openingClick) return;
    if (this.popover?.nativeElement.contains(event.target as Node)) return;
    this.closeDrill();
  }

  commitDrill() {
    const drill = this.pending;
    if (!drill || !this.explore || this.statusOf(drill.n) === 'low') return;
    this.sortColumn = null;
    this.pending = null;
    this.change(drilledInto(this.state, drill.selection, drill.n, this.rootN ?? drill.n),
      `Filtered to ${drill.selection.map(part => part.answer).join(' and ')}: ${drill.n} respondents.`);
  }

  /** Every answer of the question side by side, the one clicked ringed. */
  commitSiblings() {
    const drill = this.pending;
    if (!drill?.siblingsOf || !this.explore) return;
    const picked = drill.selection.find(part => part.variableKey === drill.siblingsOf);
    if (!picked) return;
    const next = drilledInto(this.state, [picked], drill.n, this.rootN ?? drill.n);
    this.pending = null;
    this.change({ ...next, siblings: true }, `Every ${this.questionName(picked.variableKey)} side by side.`);
  }

  toggleSiblings() {
    const state = this.state;
    if (!state || !this.explore) return;
    this.change({ ...state, siblings: !state.siblings },
      state.siblings ? 'Back to the one answer drilled into.' : 'Every answer side by side.');
  }

  /** A sibling's panel picked out of the grid: that answer becomes the drill. */
  focusSibling(table: TableModel) {
    const state = this.state;
    const last = state?.path[state.path.length - 1];
    if (!state || !last || !table.slice || !this.explore) return;
    const path = [...state.path.slice(0, -1), { ...last, answer: table.slice.label, n: table.slice.n }];
    this.change({ ...state, path, siblings: false }, `Filtered to ${table.slice.label}: ${table.slice.n} respondents.`);
  }

  goTo(index: number) {
    if (!this.state || !this.explore) return;
    this.sortColumn = null;
    const step = this.path[index - 1];
    this.change(backTo(this.state, index),
      step ? `Back to ${step.answer}: ${step.n} respondents.` : 'Back to all respondents.');
  }

  removeStep(index: number) {
    if (!this.state || !this.explore) return;
    this.sortColumn = null;
    this.change(withoutStep(this.state, index), `Removed the filter ${this.path[index]?.answer ?? ''}.`);
  }

  stepBack(event: Event) {
    if (!this.path.length) return;
    event.preventDefault();
    this.goTo(this.path.length - 1);
  }

  reset() {
    this.sortColumn = null;
    this.change(null, 'The table is back as it was published.');
  }

  // A sort is by a column's position, so it goes whenever the columns or rows change underneath it.
  setRows(key: string) {
    if (!key || !this.explore) return;
    this.sortColumn = null;
    this.explore.change({ ...this.state ?? { path: [] }, rowKey: key });
  }

  setColumns(key: string) {
    if (!this.explore) return;
    this.sortColumn = null;
    this.explore.change({
      ...this.state ?? { path: [] },
      columnKey: key === TOTAL_ONLY ? null : key || undefined,
    });
  }

  setSplit(key: string) {
    if (!this.explore) return;
    this.sortColumn = null;
    this.explore.change({ ...this.state ?? { path: [] }, layerBy: key || null, siblings: false });
  }

  setView(view: 'nested' | 'tabs' | 'grid') {
    if (!this.explore) return;
    this.explore.change({ ...this.state ?? { path: [] }, layerView: view });
  }

  sortBy(column: number) {
    this.sortColumn = this.sortColumn === column ? null : column;
    const name = this.tables[0]?.columns[column]?.name ?? '';
    this.closeDrill(true);
    this.build();
    this.say(this.sortColumn === null ? 'Rows back in their asked order.' : `Rows sorted by ${name}, largest first.`);
  }

  tabId(index: number): string {
    return `${this.panelId}-tab-${index}`;
  }

  moveTab(step: number, event: Event) {
    event.preventDefault();
    const count = this.tables.length;
    this.activeSlice = (this.activeSlice + step + count) % count;
    setTimeout(() => document.getElementById(this.tabId(this.activeSlice))?.focus());
  }

  // ---- export ----

  /** Every table as rows of plain text, marks included -- what CSV and the clipboard both carry. */
  private grid(): string[][] {
    const lines: string[][] = [];
    for (const table of this.tables) {
      if (table.slice) lines.push([`${this.layerName}: ${table.slice.label} (n=${table.slice.n})`]);
      if (table.banner.length) lines.push(['', ...table.banner.flatMap(group => Array(group.span).fill(group.label))]);
      lines.push(['', ...table.columns.map(column =>
        column.letter && this.showLetters ? `${column.name} (${column.letter})` : column.name)]);
      lines.push(['Base', ...table.columns.map(column =>
        `${column.base}${column.lowBase ? '*' : column.caution ? '!' : ''}`)]);
      if (this.weighted) {
        lines.push(['Weighted base', ...table.columns.map(column => String(column.weightedBase))]);
        lines.push(['Effective base', ...table.columns.map(column => String(column.effectiveBase))]);
      }
      for (const row of table.rows) {
        lines.push([row.label, ...row.cells.map(cell => cell.suppressed ? '··' : [
          cell.value, cell.count, cell.letters, cell.direction === 'higher' ? '▲' : cell.direction === 'lower' ? '▼' : '',
        ].filter(Boolean).join(' '))]);
      }
      lines.push([]);
    }
    if (this.path.length) {
      lines.push([`Filtered to: ${this.path.map(step => `${this.questionName(step.variableKey)} = ${step.answer}`).join(' and ')}`]);
    }
    for (const note of this.footnotes) lines.push([note]);
    return lines;
  }

  /** The table as it reads, marks included, for someone who wants it in a spreadsheet. */
  download() {
    const text = this.grid().map(cells => cells.map(csvCell).join(',')).join('\r\n');
    // A byte-order mark so Excel reads the dashes, arrows and accents as UTF-8.
    const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(this.block.header || 'crosstab').replace(/[^\w\- ]+/g, '').trim() || 'crosstab'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /** Tab-separated, which is what a spreadsheet or a slide table pastes as cells. */
  copy() {
    const text = this.grid().map(cells => cells.map(cell => cell.replace(/[\t\r\n]+/g, ' ')).join('\t')).join('\n');
    if (!navigator.clipboard) {
      this.say('Copying is not available in this browser. Use CSV instead.');
      return;
    }
    navigator.clipboard.writeText(text).then(() => {
      this.copied = true;
      this.say('Table copied. Paste it into a spreadsheet or slide.');
      setTimeout(() => { this.copied = false; }, 1500);
    }).catch(() => {
      this.copied = false;
      this.say('The table could not be copied. Use CSV instead.');
    });
  }
}
